import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs';
import { Breadcrumb } from 'src/app/models/navigation.model';
import { ContentService } from 'src/app/services/content.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics.service';
import { TranslateService } from '@ngx-translate/core';
import { PageContent } from 'src/app/models/page-content.model';

@Component({
  selector: 'app-right-navigation',
  templateUrl: './right-navigation.component.html',
  styleUrls: ['./right-navigation.component.scss']
})

export class RightNavigationComponent implements OnInit {
  pageSections: { id: string, title: string, rightNavTitle: string }[] = [];
  currentSectionId: string | null = null;
  hasSections = false;  // New property to track if sections exist
  breadcrumbs: Breadcrumb[] = [];
  addDotForFAQ = false; // Flag to add dot for FAQ section

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private contentService: ContentService,
    private navigationService: NavigationService,
    private googleAnalytics: GoogleAnalyticsService,
    public translateService: TranslateService
  ) {}

  ngOnInit(): void {
    // Extract page ID from the current route and load sections
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.route),
      map(route => {
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route;
      }),
      mergeMap(route => route.url)
    ).subscribe(() => {
      // Get the last segment of the URL to use as the page ID
      const url = this.router.url;
      if (!url) {
        this.hasSections = false;
        return;
      }
      
      // Handle case with or without hash fragments
      let pageId = '';
      if (url.includes('#')) {
        pageId = url.split('#')[0].split('/').filter(segment => segment).pop() || '';
      } else {
        pageId = url.split('/').filter(segment => segment).pop() || '';
      }
      
      if (pageId) {
        this.loadSections(pageId);
      } else {
        this.hasSections = false;
      }
    });
    
    // Subscribe to current section ID changes
    this.navigationService.getCurrentSectionId().subscribe(sectionId => {
      this.currentSectionId = sectionId;
    });
    
    // Load sections for initial route
    const url = this.router.url;
    if (url) {
      let pageId = '';
      if (url.includes('#')) {
        pageId = url.split('#')[0].split('/').filter(segment => segment).pop() || '';
      } else {
        pageId = url.split('/').filter(segment => segment).pop() || '';
      }
      
      if (pageId) {
        this.loadSections(pageId);
      } else {
        this.hasSections = false;
      }
    } else {
      this.hasSections = false;
    }


    this.navigationService.getCurrentBreadcrumbs().subscribe(breadcrumbs => {
      this.breadcrumbs = breadcrumbs;
      if (this.breadcrumbs.length > 0) {
        // Set the first breadcrumb as the current section ID if it has a valid ID
        const firstBreadcrumb = this.breadcrumbs[0];
        if (firstBreadcrumb.path == "/frequently-asked-questions") {
          this.addDotForFAQ = true;
        }
        else
        {
          this.addDotForFAQ = false;
        }
      }
    });
  }

  scrollToSection(sectionId: string): void {
    this.navigationService.scrollToSection(sectionId);

    const section = this.pageSections.find(s => s.id === sectionId);
    this.googleAnalytics.trackEvent('right_nav_click', {
      section_id: sectionId,
      section_title: section?.title ?? sectionId,
      page_path: this.router.url,
    });
  }

  private loadSections(pageId: string): void {
    if (!pageId) {
      this.hasSections = false;
      return;
    }
    
    this.contentService.getPageContent(pageId).subscribe(
      content => {
        if (content) {
          this.pageSections = content.sections;
          // Set hasSections based on whether any sections were found
          this.hasSections = this.pageSections && this.pageSections.length > 0;

          if(this.pageSections.length == 1 && this.pageSections[0].title == '')
            this.hasSections = false;
        } else {
          this.pageSections = [];
          this.hasSections = false;
        }
      },
      error => {
        console.error(`Error loading content for ${pageId}:`, error);
        this.pageSections = [];
        this.hasSections = false;
      }
    );
  }
}